import { ArrowLeft, ChevronDown, Gamepad2, Radio, Satellite } from "lucide-react";
import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { CrownTicker } from "../components/CrownTicker";
import { Footer } from "../components/Footer";
import { PayhipEmbed } from "../components/PayhipEmbed";
import { RadioBoombox } from "../components/RadioBoombox";
import { Reveal } from "../components/Reveal";
import { SectionHeading } from "../components/SectionHeading";
import { SupplyDropVideo } from "../components/SupplyDropVideo";

const schedule = [
  ["00:00", "STATIC MASS — drone hour for the wire-born"],
  ["04:00", "RUNNER'S HYMNS — anthems for anyone being hunted"],
  ["12:00", "THE PROTEST HOUR — Red Force frequencies, uncensored"],
  ["18:00", "ORB TALK — riotball scores, grudges, league records"],
  ["23:55", "SIGN-OFF THAT NEVER COMES — robots don't sleep"],
];

export default function WastelandRadio() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash !== "#radioactive-dust-drop") return undefined;
    const timer = window.setTimeout(() => {
      document.getElementById("radioactive-dust-drop")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 120);
    return () => window.clearTimeout(timer);
  }, [location.hash]);

  return (
    <main className="radio-page" data-testid="wasteland-radio-page">
      <section className="radio-hero">
        <Link to="/" className="back-link" data-testid="radio-back-link">
          <ArrowLeft size={16} /> RETURN TO WORLD
        </Link>
        <Reveal immediate>
          <p className="eyebrow">PIRATE SIGNAL // BROADCASTING FROM THE ASH</p>
          <h1>WASTELAND RADIO.</h1>
          <p>
            The last free frequency in the wasteland. No permits, no quiet hours, no off switch — just the tower,
            the static, and whatever the runners need to hear to make it through the night.
          </p>
        </Reveal>
        <Reveal className="radio-banner-frame" delay={0.12} immediate>
          <img
            src="/assets/wasteland-radio-banner.png"
            alt="Wasteland Radio — skull with headphones and a broadcast tower, one red eye lit"
            data-testid="radio-banner-image"
            fetchPriority="high"
            decoding="async"
          />
        </Reveal>
        <Reveal delay={0.2} immediate>
          <Link to="/side-games" className="radio-game-cue" data-testid="radio-game-cue">
            <Gamepad2 size={16} />
            <span>THE SIDE GAMES MOVED — DOLO'S NIGHT & DOLOSWAY LIVE AT THE ARCADE</span>
            <ChevronDown size={16} className="radio-game-cue-arrow" />
          </Link>
        </Reveal>
      </section>

      <CrownTicker />

      <section className="radio-schedule" data-testid="radio-schedule">
        <SectionHeading
          eyebrow="TRANSMISSION SCHEDULE"
          title="Show times: 24 hours a day."
          text="Because robots don't sleep. Every broadcast is pirated, every frequency borrowed, every voice a fugitive."
        />
        <div className="radio-schedule-list">
          {schedule.map(([time, show], i) => (
            <Reveal className="radio-schedule-row" delay={i * 0.06} key={time} data-testid={`radio-slot-${time.replace(":", "")}`}>
              <span className="radio-slot-time">
                <Radio size={14} /> {time}
              </span>
              <span className="radio-slot-show">{show}</span>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="radio-now-playing" data-testid="radio-now-playing">
        <SectionHeading
          eyebrow="NOW PLAYING"
          title="The deck is hot. Seventeen transmissions on loop."
          text="Punch the red transport keys, drag the volume knob, or jump straight to a frequency from the tracklist below."
        />
        <Reveal delay={0.1}>
          <RadioBoombox />
          <SupplyDropVideo />
          <PayhipEmbed />
        </Reveal>
      </section>

      <section className="radio-cta" data-testid="radio-cta">
        <Reveal>
          <p className="eyebrow">ANTHEM OF THE HUNTED</p>
          <h2>THE RUNNERS' SOUNDTRACK IS PLAYING RIGHT NOW.</h2>
          <p>Outrun the worm to the station's eternal loop. The tower keeps score.</p>
          <Link to="/wasteland-run" className="button button-primary" data-testid="radio-play-link">
            <Satellite size={16} /> TUNE IN & RUN
          </Link>
        </Reveal>
      </section>
      <Footer />
    </main>
  );
}
